import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Link2 } from "lucide-react";
import { Section } from "@/components/site/Section";
import { cn } from "@/lib/utils";
import { getAnswers, getAnswerSlug } from "@/lib/aeo/answers";

interface AnswerFaqProps {
  /** Answer ids from answers.ts to render as a visible FAQ. */
  answerIds: string[];
  eyebrow?: string;
  title?: string;
  className?: string;
  /** HTML `id` on the section for anchor links (e.g. "faq"). */
  sectionId?: string;
}

/**
 * Visible FAQ section driven by the canonical answer library.
 *
 * Renders the SAME short answers that the route's `toFaqJsonLd(answerIds)`
 * emits as FAQPage JSON-LD, so structured data always matches visible
 * content (avoids the doorway/schema-mismatch risk). Each question links
 * to its full `/fragor/<slug>` page.
 */
export function AnswerFaq({
  answerIds,
  eyebrow = "Frågor & svar",
  title = "Vanliga frågor",
  className,
  sectionId,
}: AnswerFaqProps) {
  const answers = getAnswers(answerIds);
  if (answers.length === 0) return null;

  const TitleContent = (
    <>
      {eyebrow && (
        <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </div>
      )}
      <h2 className="group/anchor inline-flex items-center gap-3 text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
        <span>{title}</span>
        {sectionId && (
          <a
            href={`#${sectionId}`}
            className="inline-flex items-center justify-center rounded-full border border-white/10 p-2 text-muted-foreground opacity-0 transition-all hover:border-mint/30 hover:text-mint group-hover/anchor:opacity-100 md:p-2.5"
            aria-label="Kopiera länk till vanliga frågor"
            onClick={(e) => {
              e.preventDefault();
              window.history.replaceState(null, "", `#${sectionId}`);
              document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <Link2 className="h-4 w-4 md:h-5 md:w-5" />
          </a>
        )}
      </h2>
    </>
  );

  return (
    <Section id={sectionId} className={className}>
      <div className="mx-auto max-w-4xl text-center">{TitleContent}</div>
      <dl className="mx-auto mt-14 grid max-w-3xl gap-4">
        {answers.map((a) => (
          <div
            key={a.id}
            className="rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-mint/30 md:p-8"
          >
            <dt className="text-xl font-semibold leading-tight text-foreground md:text-2xl">
              {a.question}
            </dt>
            <dd className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-[15px]">
              {a.shortAnswer}
              <Link
                to="/fragor/$slug"
                params={{ slug: getAnswerSlug(a) }}
                className="group ml-2 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-mint transition-colors hover:text-foreground"
              >
                Läs hela svaret
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

